import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { 
  Settings as SettingsIcon, 
  Building2, 
  User, 
  Globe,
  Save,
  AlertTriangle,
  Check,
  ChevronDown,
  Briefcase
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_API_URL}/api`;

// Available industries
const INDUSTRIES = [
  { id: "hvac", name: "HVAC Company", icon: "🔧" },
  { id: "insurance", name: "Insurance Agency", icon: "🛡️" },
  { id: "auto_repair", name: "Auto Repair", icon: "🚗" },
  { id: "grocery", name: "Grocery Store", icon: "🛒" },
  { id: "dental", name: "Dental Practice", icon: "🦷" },
  { id: "restaurant", name: "Restaurant", icon: "🍽️" },
  { id: "real_estate", name: "Real Estate", icon: "🏠" },
  { id: "legal", name: "Law Firm", icon: "⚖️" },
  { id: "healthcare", name: "Healthcare / Medical", icon: "🏥" },
  { id: "veterinary", name: "Veterinary Clinic", icon: "🐾" },
  { id: "plumbing", name: "Plumbing Company", icon: "🔧" },
  { id: "electrical", name: "Electrical Contractor", icon: "⚡" },
  { id: "property_management", name: "Property Management", icon: "🏢" },
  { id: "fitness", name: "Fitness / Gym", icon: "💪" },
  { id: "custom", name: "Custom Business", icon: "💼" }
];

export default function Settings() {
  const navigate = useNavigate();
  const { user, getAuthHeader, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showIndustryDropdown, setShowIndustryDropdown] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingIndustry, setPendingIndustry] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    industry: "",
    phone: "",
    address: "",
    website: ""
  });

  useEffect(() => {
    fetchBusinessProfile();
  }, []);

  const fetchBusinessProfile = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: getAuthHeader() });
      const business = res.data;
      setFormData({
        name: business.name || "",
        industry: business.industry || "custom",
        phone: business.phone || "",
        address: business.address || "",
        website: business.website || ""
      });
    } catch (err) {
      console.error("Failed to fetch profile:", err);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleIndustrySelect = (industryId) => {
    if (industryId !== formData.industry) {
      setPendingIndustry(industryId);
      setShowConfirmation(true);
    }
    setShowIndustryDropdown(false);
  };

  const confirmIndustryChange = () => {
    setFormData(prev => ({ ...prev, industry: pendingIndustry }));
    setShowConfirmation(false);
    setPendingIndustry(null);
  };

  const cancelIndustryChange = () => {
    setShowConfirmation(false);
    setPendingIndustry(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/business/profile`,
        formData,
        { headers: getAuthHeader() }
      );
      toast.success("Settings saved successfully");
      if (refreshUser) refreshUser();
    } catch (err) {
      console.error("Failed to save:", err);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const currentIndustry = INDUSTRIES.find(i => i.id === formData.industry) || INDUSTRIES[INDUSTRIES.length - 1];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-dots flex gap-1">
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce"></span>
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
          <SettingsIcon className="w-8 h-8 text-primary" />
          Settings
        </h1>
        <p className="text-muted-foreground mt-1">Manage your business profile and preferences</p>
      </div>

      {/* Business Profile Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Business Profile
          </CardTitle>
          <CardDescription>
            Update your business information. This affects how your AI agents represent your company.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Business Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Business Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Your Business Name"
            />
          </div>

          {/* Industry Selection */}
          <div className="space-y-2">
            <Label>Industry</Label>
            <div className="relative">
              <Button
                variant="outline"
                className="w-full justify-between"
                onClick={() => setShowIndustryDropdown(!showIndustryDropdown)}
              >
                <span className="flex items-center gap-2">
                  <span>{currentIndustry.icon}</span>
                  <span>{currentIndustry.name}</span>
                </span>
                <ChevronDown className={cn("w-4 h-4 transition-transform", showIndustryDropdown && "rotate-180")} />
              </Button>
              
              {showIndustryDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {INDUSTRIES.map((industry) => (
                    <button
                      key={industry.id}
                      className={cn(
                        "w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-accent transition-colors",
                        industry.id === formData.industry && "bg-accent"
                      )}
                      onClick={() => handleIndustrySelect(industry.id)}
                    >
                      <span>{industry.icon}</span>
                      <span>{industry.name}</span>
                      {industry.id === formData.industry && (
                        <Check className="w-4 h-4 ml-auto text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Your industry affects template recommendations. You can use any template regardless of industry.
            </p>
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone">Business Phone</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="+1 (555) 123-4567"
            />
          </div>

          {/* Website */}
          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              value={formData.website}
              onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
              placeholder="https://yourbusiness.com"
            />
          </div>

          {/* Save Button */}
          <div className="pt-4 border-t border-border">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>Saving...</>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Industry Change Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-400">
                <AlertTriangle className="w-5 h-5" />
                Change Industry?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Changing your industry will update template recommendations. Your existing agents and their configurations will not be affected.
              </p>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary">
                <span>{INDUSTRIES.find(i => i.id === pendingIndustry)?.icon}</span>
                <span className="font-medium">
                  {INDUSTRIES.find(i => i.id === pendingIndustry)?.name}
                </span>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={cancelIndustryChange}>
                  Cancel
                </Button>
                <Button className="flex-1" onClick={confirmIndustryChange}>
                  <Check className="w-4 h-4 mr-2" />
                  Confirm Change
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Links */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            Quick Links
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button variant="outline" className="justify-start" onClick={() => navigate("/dashboard/billing")}>
              💳 Manage Billing
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => navigate("/dashboard/privacy")}>
              🔒 Privacy Settings
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => navigate("/dashboard/phone")}>
              📞 Phone Setup
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
